import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Users, Search, ShieldCheck, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLAN_BADGE = {
  gold:   "bg-[#B8933A] text-black",
  bronze: "bg-[#B87333] text-white",
  free:   "bg-neutral-200 text-neutral-700",
};

export default function AdminPage() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) =>
      (u.email || "").toLowerCase().includes(s) ||
      (u.name || "").toLowerCase().includes(s) ||
      (u.plan || "").toLowerCase().includes(s),
    );
  }, [users, q]);

  const counts = useMemo(() => {
    const c = { total: users.length, free: 0, bronze: 0, gold: 0, admins: 0 };
    for (const u of users) {
      c[u.plan] = (c[u.plan] || 0) + 1;
      if (u.role === "admin") c.admins++;
    }
    return c;
  }, [users]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch { return iso.slice(0, 10); }
  };

  if (user && user.role !== "admin") return <Navigate to="/app/projects" replace />;

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow flex items-center"><span className="gold-rule" />{t("admin")}</p>
          <h1 className="mt-3 font-[Fraunces] text-4xl sm:text-5xl font-bold text-neutral-950">
            {t("admin_title")}
          </h1>
          <p className="mt-3 text-neutral-600">{t("admin_sub")}</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-sm text-[#B8933A] hover:text-[#8C6D22] font-medium flex items-center gap-1.5"
          data-testid="admin-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {t("refresh")}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat icon={Users}       label={t("total_users")}   value={counts.total} />
        <Stat                     label={t("plan_free")}     value={counts.free || 0} />
        <Stat                     label={t("plan_bronze")}   value={counts.bronze || 0} accent="bronze" />
        <Stat                     label={t("plan_gold")}     value={counts.gold || 0}   accent="gold" />
        <Stat icon={ShieldCheck}  label={t("admins")}        value={counts.admins} />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 w-4 h-4 text-neutral-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("search_users")}
          className="pl-9 h-10"
          data-testid="admin-user-search"
        />
      </div>

      {/* Table */}
      <div className="surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200 text-left">
                <Th>{t("email")}</Th>
                <Th>{t("name")}</Th>
                <Th>{t("plan")}</Th>
                <Th>{t("role")}</Th>
                <Th className="text-right">{t("projects")}</Th>
                <Th className="text-right">{t("snags")}</Th>
                <Th>{t("created")}</Th>
                <Th>{t("last_login")}</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-12 text-neutral-400">
                  <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-neutral-400">
                  {q ? t("no_matching_users") : t("no_users")}
                </td></tr>
              )}
                  {!loading && filtered.map((u) => (
                    <UserRow key={u.id} u={u} onChanged={load} fmtDate={fmtDate} lang={lang} t={t} />
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const ring = accent === "gold" ? "text-[#B8933A]" : accent === "bronze" ? "text-[#B87333]" : "text-neutral-950";
  return (
    <div className="surface-card p-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-neutral-400" strokeWidth={1.75} />}
        <p className="label-eyebrow">{label}</p>
      </div>
      <p className={`mt-1 font-[Fraunces] text-3xl font-bold tabular-nums ${ring}`}>{value}</p>
    </div>
  );
}

function UserRow({ u, onChanged, fmtDate, lang, t }) {
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(u.plan || "free");

  const changePlan = async (next) => {
    if (next === plan) return;
    if (!window.confirm(t("confirm_plan_change").replace("{email}", u.email).replace("{plan}", next.toUpperCase()))) {
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/users/${u.id}/plan`, { plan: next });
      setPlan(next);
      toast.success(t("plan_updated"));
      onChanged?.();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const isManual = u.plan_source === "manual";
  const isStripe = u.plan_source === "stripe" || !!u.stripe_subscription_id;

  return (
    <tr className="border-b border-neutral-100 hover:bg-neutral-50/60 transition-colors">
      <Td><span className="font-mono text-[13px]" data-testid={`admin-user-email-${u.id}`}>{u.email}</span></Td>
      <Td className="text-neutral-700">{u.name || "—"}</Td>
      <Td>
        <div className="flex items-center gap-2">
          <select
            value={plan}
            disabled={saving || isStripe}
            onChange={(e) => changePlan(e.target.value)}
            className={`text-[10px] font-bold uppercase tracking-widest rounded px-2 py-1 border cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 ${PLAN_BADGE[plan] || PLAN_BADGE.free}`}
            data-testid={`admin-plan-select-${u.id}`}
            title={isStripe ? t("stripe_plan_locked") : t("change_plan")}
          >
            <option value="free">FREE</option>
            <option value="bronze">BRONZE</option>
            <option value="gold">GOLD</option>
          </select>
          {isManual && (
            <span className="text-[9px] font-bold uppercase tracking-widest text-[#B8933A]" title={t("manual_plan_tip")}>
              {t("manual_badge")}
            </span>
          )}
          {isStripe && (
            <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-500" title={t("stripe_plan_locked")}>
              STRIPE
            </span>
          )}
        </div>
      </Td>
      <Td>
        <span className={`text-xs ${u.role === "admin" ? "text-[#B8933A] font-semibold" : "text-neutral-500"}`}>
          {u.role || "user"}
        </span>
      </Td>
      <Td className="text-right tabular-nums">{u.project_count ?? 0}</Td>
      <Td className="text-right tabular-nums">{u.snag_count ?? 0}</Td>
      <Td className="text-neutral-500 text-xs">{fmtDate(u.created_at)}</Td>
      <Td className="text-neutral-500 text-xs">{u.last_login ? fmtDate(u.last_login) : "—"}</Td>
    </tr>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 label-eyebrow font-medium text-neutral-500 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
